import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Users,
  Search,
  AlertTriangle,
  Target,
  ShoppingCart,
  DollarSign,
  PieChart,
  ChevronRight,
  User,
  Lock,
  Zap,
  Sparkles,
  ChevronDown,
} from "lucide-react";

// Market Share Progress Bar component
const MarketShareBar = ({ percentage }) => {
  // Extract numeric value if it's a string
  const numericValue = typeof percentage === "string" ? parseInt(percentage.replace(/\D/g, "")) : percentage || 0;

  return (
    <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-1000"
        style={{ width: `${numericValue}%` }}
      />
    </div>
  );
};

export function PersonasAggregateCard({ details, isPremium = false, insightCredits = 0 }) {
  // Extract personas array and keywords from the details prop
  const personas = Array.isArray(details?.data) ? details.data : [];
  const queryKeywords = details?.query_keywords || [];

  // Don't render if no personas are found
  if (!personas || personas.length === 0) {
    return null;
  }

  // Determine if user can view all personas
  const canViewAllPersonas = isPremium || insightCredits > 0;

  // Calculate how many additional personas are locked
  const additionalPersonasCount = personas.length - 1;
  const hasAdditionalPersonas = additionalPersonasCount > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl border border-gray-100 group hover:border-blue-200 mb-8 relative"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-48 h-48 bg-blue-50 rounded-full opacity-20 -mt-20 -mr-20 z-0"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-50 rounded-full opacity-20 -mb-32 -ml-32 z-0"></div>

      {/* Header bar */}
      <div className="h-1.5 bg-gradient-to-r from-blue-400 to-blue-600 w-full"></div>

      {/* Free Preview Badge - Only show when not premium and has additional personas */}
      {!canViewAllPersonas && hasAdditionalPersonas && (
        <div className="absolute top-4 left-4 z-10">
          <div className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-1.5 rounded-lg shadow-sm flex items-center">
            <Sparkles size={12} className="mr-1" /> Preview
          </div>
        </div>
      )}

      <div className="p-6 sm:p-8 relative z-10">
        {/* Card Header */}
        <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 mr-4 shadow-sm">
              <Users size={22} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300">
                Target Audience Personas
              </h3>
              <p className="text-gray-500 text-sm mt-0.5">
                {personas.length} identified {personas.length === 1 ? "persona" : "personas"} for your market
              </p>
            </div>
          </div>

          {/* Query Keywords Badge */}
          {queryKeywords.length > 0 && (
            <div className="flex items-center bg-gray-50 hover:bg-gray-100 transition-colors px-3 py-2 rounded-lg border border-gray-100">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center mr-2">
                <Search size={14} className="text-blue-500" />
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Generated for</span>
                <span className="text-sm font-medium text-gray-700 truncate max-w-[180px] block">{queryKeywords.join(", ")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Primary Persona - Always Visible */}
        {personas.length > 0 &&
          (() => {
            const persona = personas[0];

            return (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-blue-50 rounded-xl p-6 border border-blue-100 shadow-sm hover:shadow-md transition-shadow duration-300 mb-6"
              >
                {/* Persona Header */}
                <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm mr-3">
                      <User size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-blue-800">{persona.persona_title || "Primary Persona"}</h4>
                      {persona.market_percentage && (
                        <div className="flex items-center mt-1">
                          <PieChart size={12} className="text-gray-500 mr-1.5" />
                          <span className="text-xs font-medium text-gray-600">{persona.market_percentage} of Total Market</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Budget Badge */}
                  {persona.monthly_budget && (
                    <div className="px-3 py-1.5 bg-white rounded-lg shadow-sm border border-opacity-50 border-gray-200">
                      <div className="flex items-center">
                        <DollarSign size={14} className="text-green-600 mr-1" />
                        <span className="text-sm font-semibold text-gray-800">
                          {persona.monthly_budget}
                          <span className="text-xs text-gray-500 ml-1">/ month</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Market Share Bar if percentage exists */}
                {persona.market_percentage && (
                  <div className="mb-4">
                    <MarketShareBar percentage={persona.market_percentage} />
                  </div>
                )}

                {/* Background */}
                {persona.background && (
                  <div className="bg-white bg-opacity-60 rounded-lg p-4 backdrop-blur-sm mb-5 border border-opacity-40 border-blue-100">
                    <p className="text-gray-700 text-sm leading-relaxed">{persona.background}</p>
                  </div>
                )}

                {/* Key Data Display - 2x2 Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  {/* Pain Points */}
                  {persona.pain_points && persona.pain_points.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center mr-2">
                          <AlertTriangle size={15} className="text-red-600" />
                        </div>
                        <span className="text-sm font-semibold text-red-700">Key Pain Points</span>
                      </div>
                      <ul className="space-y-2">
                        {persona.pain_points.map((item, idx) => (
                          <li key={idx} className="flex items-start group">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 mr-2.5 flex-shrink-0"></div>
                            <span className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-900 transition-colors">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Motivations */}
                  {persona.motivations && persona.motivations.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-2">
                          <Target size={15} className="text-green-600" />
                        </div>
                        <span className="text-sm font-semibold text-green-700">Primary Motivations</span>
                      </div>
                      <ul className="space-y-2">
                        {persona.motivations.map((item, idx) => (
                          <li key={idx} className="flex items-start group">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 mr-2.5 flex-shrink-0"></div>
                            <span className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-900 transition-colors">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Buying Behavior & Decision Triggers - Simplified */}
                <div className="grid grid-cols-1 gap-4">
                  {/* Behavior & Triggers - Condensed View */}
                  <div className="bg-white bg-opacity-60 rounded-lg p-4 border border-opacity-30 border-blue-100">
                    {persona.buying_behavior && (
                      <div className="mb-3">
                        <div className="flex mb-1.5 items-center">
                          <ShoppingCart size={15} className="text-blue-600 mr-1.5" />
                          <span className="text-xs uppercase tracking-wide font-semibold text-blue-700">Buying Behavior</span>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{persona.buying_behavior}</p>
                      </div>
                    )}

                    {persona.decision_triggers && (
                      <div>
                        <div className="flex mb-1.5 items-center">
                          <ChevronRight size={15} className="text-blue-600 mr-1.5" />
                          <span className="text-xs uppercase tracking-wide font-semibold text-blue-700">Decision Triggers</span>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{persona.decision_triggers}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })()}

        {/* Additional Personas Teaser - When There Are More */}
        {hasAdditionalPersonas && (
          <>
            {/* Personas Preview Banner */}
            {!canViewAllPersonas && (
              <div className="mb-4 bg-gray-50 border border-gray-100 rounded-xl p-4">
                <div className="flex items-center mb-2">
                  <ChevronDown size={16} className="text-gray-500 mr-2" />
                  <h4 className="text-sm font-semibold text-gray-700">Additional Personas Include:</h4>
                </div>
                <div className="space-y-2 pl-4">
                  {personas.slice(1).map((persona, idx) => (
                    <div key={idx} className="flex items-center text-xs text-gray-500">
                      <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
                      <span>{persona.persona_title || `Secondary Persona ${idx + 1}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Content Block - Only for non-premium users */}
            {!canViewAllPersonas ? (
              // Paywall for non-premium users
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-24 h-24 bg-blue-200 rounded-full opacity-20 -mt-12 -ml-12"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-indigo-200 rounded-full opacity-20 -mb-16 -mr-16"></div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    <Lock size={24} className="text-blue-600" />
                  </div>

                  <h4 className="text-xl font-bold text-blue-800 mb-2">
                    {additionalPersonasCount} More {additionalPersonasCount > 1 ? "Personas" : "Persona"} Available
                  </h4>

                  <p className="text-blue-700 mb-6 max-w-md">
                    Unlock additional audience personas to fully understand your target market and create more effective marketing
                    strategies.
                  </p>

                  <Link
                    to="/credits/"
                    className="flex items-center bg-gradient-to-r from-blue-500 to-blue-600 hover:brightness-105 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 shadow-md group"
                  >
                    <Zap size={18} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                    Unlock All Personas
                    <ChevronRight
                      size={16}
                      className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                    />
                  </Link>
                </div>
              </div>
            ) : (
              // Show all additional personas for premium users
              <div className="space-y-5">
                {personas.slice(1).map((persona, index) => {
                  // Calculate different color themes for variety
                  const colors = [
                    {
                      bg: "bg-purple-50",
                      border: "border-purple-100",
                      title: "text-purple-800",
                      accent: "text-purple-600",
                      accentBg: "bg-purple-50",
                    },
                    {
                      bg: "bg-emerald-50",
                      border: "border-emerald-100",
                      title: "text-emerald-800",
                      accent: "text-emerald-600",
                      accentBg: "bg-emerald-50",
                    },
                    {
                      bg: "bg-amber-50",
                      border: "border-amber-100",
                      title: "text-amber-800",
                      accent: "text-amber-600",
                      accentBg: "bg-amber-50",
                    },
                  ];
                  const theme = colors[index % colors.length];

                  return (
                    <motion.div
                      key={persona.persona_title || `persona-${index + 1}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                      className={`${theme.bg} rounded-xl p-6 border ${theme.border} shadow-sm hover:shadow-md transition-shadow duration-300`}
                    >
                      {/* Persona Header */}
                      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm mr-3">
                            <User size={18} className={theme.accent} />
                          </div>
                          <div>
                            <h4 className={`text-lg font-bold ${theme.title}`}>{persona.persona_title || `Persona ${index + 2}`}</h4>
                            {persona.market_percentage && (
                              <div className="flex items-center mt-1">
                                <PieChart size={12} className="text-gray-500 mr-1.5" />
                                <span className="text-xs font-medium text-gray-600">{persona.market_percentage} of Total Market</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Budget Badge */}
                        {persona.monthly_budget && (
                          <div className="px-3 py-1.5 bg-white rounded-lg shadow-sm border border-opacity-50 border-gray-200">
                            <div className="flex items-center">
                              <DollarSign size={14} className="text-green-600 mr-1" />
                              <span className="text-sm font-semibold text-gray-800">
                                {persona.monthly_budget}
                                <span className="text-xs text-gray-500 ml-1">/ month</span>
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Market Share Bar if percentage exists */}
                      {persona.market_percentage && (
                        <div className="mb-4">
                          <MarketShareBar percentage={persona.market_percentage} />
                        </div>
                      )}

                      {/* Background */}
                      {persona.background && (
                        <div className="bg-white bg-opacity-60 rounded-lg p-4 backdrop-blur-sm mb-5 border border-opacity-40 border-gray-200">
                          <p className="text-gray-700 text-sm leading-relaxed">{persona.background}</p>
                        </div>
                      )}

                      {/* Key Data Display - 2x2 Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                        {/* Pain Points */}
                        {persona.pain_points && persona.pain_points.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                            <div className="flex items-center mb-3">
                              <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center mr-2">
                                <AlertTriangle size={15} className="text-red-600" />
                              </div>
                              <span className="text-sm font-semibold text-red-700">Key Pain Points</span>
                            </div>
                            <ul className="space-y-2">
                              {persona.pain_points.map((item, idx) => (
                                <li key={idx} className="flex items-start group">
                                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 mr-2.5 flex-shrink-0"></div>
                                  <span className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-900 transition-colors">
                                    {item}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Motivations */}
                        {persona.motivations && persona.motivations.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                            <div className="flex items-center mb-3">
                              <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-2">
                                <Target size={15} className="text-green-600" />
                              </div>
                              <span className="text-sm font-semibold text-green-700">Primary Motivations</span>
                            </div>
                            <ul className="space-y-2">
                              {persona.motivations.map((item, idx) => (
                                <li key={idx} className="flex items-start group">
                                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 mr-2.5 flex-shrink-0"></div>
                                  <span className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-900 transition-colors">
                                    {item}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Buying Behavior & Decision Triggers - Simplified */}
                      <div className="grid grid-cols-1 gap-4">
                        {/* Behavior & Triggers - Condensed View */}
                        <div className="bg-white bg-opacity-60 rounded-lg p-4 border border-opacity-30 border-gray-200">
                          {persona.buying_behavior && (
                            <div className="mb-3">
                              <div className="flex mb-1.5 items-center">
                                <ShoppingCart size={15} />
                                <span className={`text-xs uppercase tracking-wide font-semibold ${theme.accent}`}>Buying Behavior</span>
                              </div>
                              <p className="text-gray-700 text-sm leading-relaxed">{persona.buying_behavior}</p>
                            </div>
                          )}

                          {persona.decision_triggers && (
                            <div>
                              <div className="flex mb-1.5 items-center">
                                <ChevronRight size={15} className={theme.accent} />
                                <span className={`text-xs uppercase tracking-wide font-semibold ${theme.accent}`}>Decision Triggers</span>
                              </div>
                              <p className="text-gray-700 text-sm leading-relaxed">{persona.decision_triggers}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}
